import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackVisit, getAnalyticsData, AnalyticsData } from '../apis/analytics';

interface AnalyticsContextType {
  analyticsData: AnalyticsData;
  refreshAnalyticsData: () => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalVisitors: 0,
    pageViews: 0,
    workPageViews: {}
  });
  const location = useLocation();
  const previousPathRef = useRef<string>('');
  const isProcessingRef = useRef<boolean>(false);

  // 통합된 페이지 방문 추적 - 한 번만 호출
  useEffect(() => {
    const trackPageVisit = async () => {
      // 이미 처리 중이거나 같은 경로의 재렌더링인 경우 중복 호출 방지
      if (isProcessingRef.current || previousPathRef.current === location.pathname) {
        return;
      }

      isProcessingRef.current = true;
      const currentPath = location.pathname;
      previousPathRef.current = currentPath;
      
      console.log(`[Analytics] Tracking visit for path: ${currentPath}`);
      
      try {
        // work detail 페이지인 경우 WorkDetail 컴포넌트에서 처리하므로 여기서는 호출하지 않음
        if (currentPath.startsWith('/works/') && currentPath.split('/works/')[1]) {
          const slugOrId = currentPath.split('/works/')[1];
          const cleanSlugOrId = slugOrId?.split(/[?#&]/)[0];
          
          if (cleanSlugOrId) {
            console.log(`[Analytics] Work detail page detected with slug: ${cleanSlugOrId}, skipping general visit tracking`);
            // WorkDetail 컴포넌트에서 workId와 함께 호출하므로 여기서는 호출하지 않음
            return;
          }
        }
        
        // 일반 페이지나 works 목록 페이지는 여기서 처리
        await trackVisit(currentPath);
      } catch (error) {
        console.error('[Analytics] Error tracking visit:', error);
      } finally {
        // 호출 완료 후 플래그 초기화 (오류 발생해도 초기화 필요)
        isProcessingRef.current = false;
      }
    };

    // 약간의 지연 시간을 두고 호출 (이벤트 루프 끝나고 실행)
    const timerId = setTimeout(() => {
      trackPageVisit();
    }, 100);

    return () => {
      clearTimeout(timerId);
    };
  }, [location.pathname]);

  // 분석 데이터 가져오기
  const refreshAnalyticsData = async () => {
    const data = await getAnalyticsData();
    setAnalyticsData(data);
  };

  // 초기 데이터 로드
  useEffect(() => {
    refreshAnalyticsData();
  }, []);

  return (
    <AnalyticsContext.Provider value={{ analyticsData, refreshAnalyticsData }}>
      {children}
    </AnalyticsContext.Provider>
  );
}; 